/**
 * Coordinates - Represents coordinate system information with start and end points.
 * Used to store coordinate system names, types, and X/Y/Z coordinates.
 */

/**
 * XYZ coordinate point
 */
export interface XYZ {
  /** X coordinate */
  x: number;
  /** Y coordinate */
  y: number;
  /** Z coordinate */
  z: number;
}

export class Coordinates {
  /** Coordinate system name */
  SystemName: string;

  /** Coordinate type */
  Type: string;

  /** Starting X coordinate */
  XStart: number;

  /** Starting Y coordinate */
  YStart: number;

  /** Starting XYZ coordinates */
  XYZStart: XYZ;

  /** Ending X coordinate */
  XEnd: number;

  /** Ending Y coordinate */
  YEnd: number;

  /** Ending XYZ coordinates */
  XYZEnd: XYZ;

  /**
   * Creates a new Coordinates instance
   * @param data - Object containing coordinate properties
   */
  constructor(data: {
    SystemName: string;
    Type: string;
    XStart: number;
    YStart: number;
    XYZStart: XYZ;
    XEnd: number;
    YEnd: number;
    XYZEnd: XYZ;
  }) {
    this.SystemName = data.SystemName;
    this.Type = data.Type;
    this.XStart = data.XStart;
    this.YStart = data.YStart;
    this.XYZStart = data.XYZStart;
    this.XEnd = data.XEnd;
    this.YEnd = data.YEnd;
    this.XYZEnd = data.XYZEnd;
  }

  /**
   * Creates a Coordinates from a plain object (useful for deserialization)
   * @param obj - Plain object with coordinate data
   * @returns New Coordinates instance
   */
  static fromObject(obj: Partial<Coordinates>): Coordinates {
    const createXYZ = (xyz: any): XYZ => {
      if (xyz && typeof xyz === "object" && "x" in xyz && "y" in xyz && "z" in xyz) {
        return {
          x: typeof xyz.x === "number" ? xyz.x : 0,
          y: typeof xyz.y === "number" ? xyz.y : 0,
          z: typeof xyz.z === "number" ? xyz.z : 0,
        };
      }
      return { x: 0, y: 0, z: 0 };
    };

    return new Coordinates({
      SystemName: obj.SystemName ?? "",
      Type: obj.Type ?? "",
      XStart: obj.XStart ?? 0,
      YStart: obj.YStart ?? 0,
      XYZStart: createXYZ(obj.XYZStart),
      XEnd: obj.XEnd ?? 0,
      YEnd: obj.YEnd ?? 0,
      XYZEnd: createXYZ(obj.XYZEnd),
    });
  }

  /**
   * Converts the Coordinates to a plain object (useful for serialization)
   * @returns Plain object representation
   */
  toObject(): Record<string, unknown> {
    return {
      SystemName: this.SystemName,
      Type: this.Type,
      XStart: this.XStart,
      YStart: this.YStart,
      XYZStart: this.XYZStart,
      XEnd: this.XEnd,
      YEnd: this.YEnd,
      XYZEnd: this.XYZEnd,
    };
  }
}

